// returningItems — VQE-Edit edit-actions for the RETURNING expression list via
// SR-Edit's `add-returning-item` / `remove-returning-item` ops (§2.4 S6b).
// Statement-kind-agnostic by construction (INSERT / UPDATE / DELETE all
// populate `structure.returning`), though only the INSERT `ReturningCard`
// mounts them in v1 (decision 11). `add` appends onto the existing list or
// synthesizes ` RETURNING <text>` at the statement tail when none exists —
// so it needs no clause-presence validation; `remove` of the only item drops
// the RETURNING keyword too (the applicator owns that). These modules only
// validate inputs against an optional structure and delegate to `commitEdit`
// (validation throws BEFORE commit; the commit itself never rejects).
//
// PRD: Ralph/VisualQueryEditorEdit/PRD.md § Slice: Edit — DML edits.

import type { QueryStructure } from '../../../../sql-resolver/types';
import { commitEdit, type CommitEditCtx, type CommitOutcome } from '../commitEdit';

export type ReturningItemOptions = {
  /** Optional structure used for index-bounds validation. */
  readonly structure?: QueryStructure;
};

export async function addReturningItem(
  text: string,
  ctx: CommitEditCtx,
  options: ReturningItemOptions = {},
): Promise<CommitOutcome> {
  void options;
  const t = text.trim();
  if (t.length === 0) {
    throw new Error('addReturningItem: text must not be empty');
  }
  return commitEdit({ op: 'add-returning-item', text: t }, ctx);
}

export async function removeReturningItem(
  index: number,
  ctx: CommitEditCtx,
  options: ReturningItemOptions = {},
): Promise<CommitOutcome> {
  if (!Number.isInteger(index) || index < 0) {
    throw new Error(
      `removeReturningItem: index must be a non-negative integer (got ${index})`,
    );
  }
  if (options.structure) {
    const items = options.structure.returning ?? [];
    if (items.length === 0) {
      throw new Error('removeReturningItem: RETURNING list is empty');
    }
    if (index >= items.length) {
      throw new Error(
        `removeReturningItem: index ${index} out of range (RETURNING list has ${items.length} item(s))`,
      );
    }
  }
  return commitEdit({ op: 'remove-returning-item', index }, ctx);
}
